import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Star, Clock, Calendar as CalendarIcon, CheckCircle2, GitCompare, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ChatPreview from '../components/ChatPreview';

const BidsView = () => {
  const { requestId } = useParams();
  const { API, sessionToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [request, setRequest] = useState(null);
  const [talleres, setTalleres] = useState({});
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [starredBidIds, setStarredBidIds] = useState(new Set());
  const [starringBid, setStarringBid] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatTaller, setChatTaller] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [bidsRes, requestRes] = await Promise.all([
        axios.get(`${API}/bids?service_request_id=${requestId}`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }),
        axios.get(`${API}/service-requests/${requestId}`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        })
      ]);

      setBids(bidsRes.data);
      setRequest(requestRes.data);

      // Fetch taller info
      const tallerIds = [...new Set(bidsRes.data.map(b => b.taller_id))];
      const tallerData = {};
      await Promise.all(tallerIds.map(async (tid) => {
        try {
          const tRes = await axios.get(`${API}/public/talleres/${tid}`);
          tallerData[tid] = tRes.data;
        } catch (e) { console.error('Error fetching taller info:', e); }
      }));
      setTalleres(tallerData);
    } catch (error) {
      console.error('Error fetching bids:', error);
    }
  }, [API, sessionToken, requestId]);

  const fetchStarredBids = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/starred-bids?request_id=${requestId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setStarredBidIds(new Set(res.data.map(b => b.id)));
    } catch (error) {
      console.error('Error fetching starred bids:', error);
    }
  }, [API, sessionToken, requestId]);

  useEffect(() => {
    fetchData();
    fetchStarredBids();
  }, [fetchData, fetchStarredBids]);

  const toggleStar = async (bidId) => {
    try {
      setStarringBid(bidId);
      const res = await axios.post(`${API}/bids/${bidId}/star`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (res.data.starred) {
        setStarredBidIds(prev => new Set([...prev, bidId]));
        toast.success('Oferta marcada para comparar');
      } else {
        setStarredBidIds(prev => {
          const next = new Set(prev);
          next.delete(bidId);
          return next;
        });
        toast.success('Oferta desmarcada');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail === 'Máximo 4 ofertas marcadas para comparar por solicitud') {
        toast.error('Máximo 4 ofertas para comparar');
      } else {
        toast.error('Error al marcar oferta');
      }
    } finally {
      setStarringBid(null);
    }
  };

  const openChat = (bid, taller) => {
    setChatTaller({ name: taller?.company_name || taller?.name || 'Taller', bid });
    setChatOpen(true);
  };

  const handleAcceptBid = async () => {
    if (!selectedDate) {
      toast.error('Selecciona una fecha propuesta por el taller');
      return;
    }
    try {
      setAccepting(true);
      await axios.post(
        `${API}/bids/${selectedBid.id}/accept`,
        {
          vehicle_id: request.vehicle_id,
          scheduled_date: selectedDate,
        },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );

      toast.success('Oferta aceptada. Cita programada exitosamente');
      setShowAcceptDialog(false);
      setSelectedDate(null);
      navigate('/client/dashboard');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al aceptar la oferta');
    } finally {
      setAccepting(false);
    }
  };

  const openAcceptDialog = (bid) => {
    setSelectedBid(bid);
    setSelectedDate(null);
    setShowAcceptDialog(true);
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('es-CR', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/client/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit' }}>
              Ofertas Recibidas
            </h1>
          </div>
          {starredBidIds.size > 0 && (
            <Button
              onClick={() => navigate(`/client/compare?request_id=${requestId}`)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="compare-offers-btn"
            >
              <GitCompare className="w-4 h-4 mr-2" />
              Comparar ({starredBidIds.size})
            </Button>
          )}
        </div>

        {request && (
          <Card className="p-6 mb-6">
            <h3 className="text-xl font-semibold mb-2">{request.service_type}</h3>
            <p className="text-slate-600 mb-4">{request.description}</p>
            <div className="flex items-center space-x-4 text-sm text-slate-500">
              <span>Urgencia: {request.urgency}</span>
              <span>{request.location?.city}</span>
              <Badge>{bids.length} ofertas</Badge>
            </div>
          </Card>
        )}

        {bids.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-slate-500">Aún no has recibido ofertas para esta solicitud</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {bids.map((bid) => {
              const taller = talleres[bid.taller_id];
              const hasDates = bid.proposed_dates && bid.proposed_dates.length > 0;
              const isStarred = starredBidIds.has(bid.id);
              const isStarring = starringBid === bid.id;
              return (
                <Card key={bid.id} className={`p-6 hover:shadow-lg transition-shadow relative ${isStarred ? 'ring-2 ring-purple-400' : ''}`} data-testid={`bid-card-${bid.id}`}>
                  {/* Star button - top right corner */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`absolute top-3 right-3 p-2 h-auto ${isStarred ? 'text-yellow-500' : 'text-slate-300 hover:text-yellow-500'}`}
                    onClick={() => toggleStar(bid.id)}
                    disabled={isStarring}
                    data-testid={`star-btn-${bid.id}`}
                  >
                    <Star className={`w-5 h-5 ${isStarred ? 'fill-current' : ''}`} />
                  </Button>

                  <div className="flex justify-between items-start mb-4 pr-10">
                    <div>
                      <h3 className="text-2xl font-bold text-purple-600 mb-1">
                        ₡{bid.price.toLocaleString()}
                      </h3>
                      <p className="text-sm font-medium text-slate-700">
                        {taller?.company_name || taller?.name || 'Taller'}
                      </p>
                      <div className="flex items-center space-x-2 text-sm text-slate-600 mt-1">
                        <Clock className="w-4 h-4" />
                        <span>{bid.estimated_time}</span>
                      </div>
                    </div>
                    <Badge className={bid.status === 'accepted' ? 'bg-green-100 text-green-800' : bid.status === 'rejected' ? 'bg-slate-100 text-slate-600' : 'bg-blue-100 text-blue-800'}>
                      {bid.status === 'pending' ? 'pendiente' : bid.status === 'accepted' ? 'aceptada' : 'rechazada'}
                    </Badge>
                  </div>

                  <div className="mb-4">
                    <p className="text-slate-700 text-sm">{bid.message}</p>
                  </div>

                  {hasDates && (
                    <div className="mb-4 bg-purple-50 rounded-lg p-3">
                      <p className="text-xs font-medium text-purple-700 mb-2 flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" /> Fechas propuestas por el taller
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {bid.proposed_dates.slice(0, 3).map((d) => (
                          <Badge key={d} variant="outline" className="text-xs bg-white">
                            {formatDate(d)}
                          </Badge>
                        ))}
                        {bid.proposed_dates.length > 3 && (
                          <Badge variant="outline" className="text-xs bg-white">+{bid.proposed_dates.length - 3} más</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Star className="w-5 h-5 text-yellow-500 fill-current" />
                        <span className="text-sm">{(taller?.rating || 0).toFixed(1)} ({taller?.total_reviews || 0})</span>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Chat button */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openChat(bid, taller)}
                          data-testid={`chat-btn-${bid.id}`}
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          Chat
                        </Button>

                        {bid.status === 'pending' && (
                          <Button onClick={() => openAcceptDialog(bid)} className="bg-green-600 hover:bg-green-700" data-testid={`accept-bid-btn-${bid.id}`}>
                            Aceptar Oferta
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Calendar / accept dialog */}
        <Dialog open={showAcceptDialog} onOpenChange={(o) => { setShowAcceptDialog(o); if (!o) setSelectedDate(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Agendar cita</DialogTitle>
            </DialogHeader>

            {selectedBid && (
              <div className="space-y-4 mt-2">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm text-slate-600">Oferta seleccionada</p>
                  <p className="text-2xl font-bold text-purple-700">₡{selectedBid.price.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{selectedBid.estimated_time}</p>
                </div>

                {selectedBid.proposed_dates && selectedBid.proposed_dates.length > 0 ? (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">
                      Selecciona una fecha propuesta por el taller
                    </p>
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {selectedBid.proposed_dates.map((d, idx) => {
                        const iso = new Date(d).toISOString();
                        const isSelected = selectedDate === iso;
                        return (
                          <button
                            key={`date-${iso}`}
                            type="button"
                            onClick={() => setSelectedDate(iso)}
                            className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
                              isSelected
                                ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-200'
                                : 'border-slate-200 hover:border-purple-300 hover:bg-slate-50'
                            }`}
                            data-testid={`date-option-${idx}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-slate-900">{formatDate(d)}</p>
                              </div>
                              {isSelected && <CheckCircle2 className="w-5 h-5 text-purple-600" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      Este taller aún no propuso fechas. Solicítale fechas mediante chat o contacto directo.
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleAcceptBid}
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={!selectedDate || accepting || !(selectedBid.proposed_dates && selectedBid.proposed_dates.length)}
                  data-testid="confirm-accept-btn"
                >
                  {accepting ? 'Procesando…' : 'Confirmar cita'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Chat Preview Modal */}
        <ChatPreview
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          otherPartyName={chatTaller?.name}
          otherPartyRole="taller"
          contextInfo={request ? `${request.service_type} - ${request.description?.slice(0, 40)}...` : ''}
        />
      </div>
    </div>
  );
};

export default BidsView;
